import { Button } from "@/components/flexkit/Button";
import { CheckboxInputField } from "@/components/flexkit/checkbox/CheckboxInputField";
import { Text } from "@/components/podkit/typography/Text";
import { WorkflowTrigger_PullRequestEvent } from "gitpod-next-api/gitpod/v1/workflow_pb";
import { useState, type FC } from "react";
import { ConfigureWebhookDialog } from "@/components/automations/ConfigureWebhookDialog";
import type { WorkflowEditFormTriggerData } from "@/components/automations/automation-edit-form-data";
import { WorkflowEditCodeContext } from "@/components/automations/AutomationEditCodeContext";
import { pullRequestEventName } from "@/utils/automations";
import type { PlainWorkflow } from "@/queries/automation-queries";
import { useFlagReusableWebhooksEnabled } from "@/hooks/use-flag";
import { WebhookSelector } from "@/components/automations/WebhookSelector";

type WorkflowEditTriggerPullRequestProps = {
    workflow: PlainWorkflow;
    trigger: WorkflowEditFormTriggerData;
    disabled?: boolean;
    handleConfigChange: (config: WorkflowEditFormTriggerData) => void;
};

export const WorkflowEditTriggerPullRequest: FC<WorkflowEditTriggerPullRequestProps> = ({
    workflow,
    trigger,
    handleConfigChange,
    disabled = false,
}) => {
    const { value: isReusableWebhooksEnabled } = useFlagReusableWebhooksEnabled();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedEvents, setSelectedEvents] = useState<Set<WorkflowTrigger_PullRequestEvent>>(
        new Set(trigger.config?.pullRequest?.events),
    );

    const handleEventToggle = (event: WorkflowTrigger_PullRequestEvent, checked: boolean) => {
        const newSelectedEvents = new Set(selectedEvents);
        if (checked) {
            newSelectedEvents.add(event);
        } else {
            newSelectedEvents.delete(event);
        }

        handleConfigChange({
            ...trigger,
            config: {
                pullRequest: {
                    events: Array.from(newSelectedEvents),
                    webhookId: trigger.config?.pullRequest?.webhookId,
                },
            },
        });
        setSelectedEvents(newSelectedEvents);
    };

    const handleWebhookChange = (webhookId: string | undefined) => {
        handleConfigChange({
            ...trigger,
            config: {
                pullRequest: {
                    events: trigger.config?.pullRequest?.events ?? [],
                    webhookId,
                },
            },
        });
    };

    return (
        <div className="flex flex-col gap-4">
            <Text className="text-sm text-content-secondary">
                Ona listens for pull-request events from your Git provider.
            </Text>

            {isReusableWebhooksEnabled ? (
                <div className="flex flex-col gap-2">
                    <Text className="text-base font-medium text-content-primary">Webhook</Text>
                    <WebhookSelector
                        selectedWebhookId={trigger.config?.pullRequest?.webhookId}
                        disabled={disabled}
                        onSelectionChange={handleWebhookChange}
                    />
                </div>
            ) : (
                <div className="flex flex-col gap-1">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setIsDialogOpen(true)}
                        disabled={disabled || !workflow.id}
                        className="w-fit"
                        data-testid="configure-webhook-button"
                        data-tracking-id="configure-webhook-pull-request-trigger-config"
                    >
                        Configuration
                    </Button>
                    {!workflow.id && (
                        <Text className="text-xs text-content-secondary">
                            Save the automation first to configure the webhook.
                        </Text>
                    )}
                </div>
            )}

            <div className="flex flex-col gap-4">
                <WorkflowEditCodeContext
                    context={trigger.context}
                    disabled={disabled}
                    onChange={(context) => handleConfigChange({ ...trigger, context: context })}
                />

                <div className="flex flex-col gap-2">
                    <Text className="text-base font-medium text-content-primary">Triggers on PR:</Text>
                    <div className="flex flex-row flex-wrap gap-4">
                        {[
                            WorkflowTrigger_PullRequestEvent.OPENED,
                            WorkflowTrigger_PullRequestEvent.UPDATED,
                            WorkflowTrigger_PullRequestEvent.READY_FOR_REVIEW,
                            WorkflowTrigger_PullRequestEvent.APPROVED,
                            WorkflowTrigger_PullRequestEvent.MERGED,
                            WorkflowTrigger_PullRequestEvent.CLOSED,
                        ].map((event) => (
                            <CheckboxInputField
                                key={event}
                                label={pullRequestEventName(event)}
                                checked={selectedEvents.has(event)}
                                onChange={(checked) => handleEventToggle(event, checked)}
                                disabled={disabled}
                                data-testid={`pr-event-${pullRequestEventName(event).toLowerCase().replace(/\s+/g, "-")}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
            <ConfigureWebhookDialog workflow={workflow} open={isDialogOpen} onClose={() => setIsDialogOpen(false)} />
        </div>
    );
};
